import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { alpha, Box, ButtonBase, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import type {
  ClientPortalCampaign,
  ClientPortalSubcampaign,
} from "../../store/apis/clientPortalApi";
import {
  ChartModeSwitch,
  ClientCard,
  PillToggle,
  useChartMode,
} from "./ClientUiViewClient";
import {
  allSubcampaigns,
  ChartMode,
  formatCompact,
  formatMoney,
  formatNumber,
  formatPercent,
  formatShortDate,
  formatUnitMoney,
  MAX_SERIES,
  seriesColor,
  toDailySeries,
} from "./clientFormatViewClient";
import DateRangePickerViewClient, {
  DateRange,
  RangeBounds,
  rangeDays,
  resolveRange,
} from "./DateRangePickerViewClient";
import {
  LegendStroke,
  progressSeriesElements,
} from "./ProgressSeriesViewClient";

const CHART_HEIGHT = 320;

/** Lo que se pinta: impresiones, clics, el CTR que sale de ambos o la inversión. */
export type MonthMetric = "impressions" | "clicks" | "ctr" | "investment";
const METRICS: MonthMetric[] = ["impressions", "clicks", "ctr"];

type Row = { date: string } & Record<string, number | null | string>;

interface Series {
  key: string;
  /** Id de la subcampaña: la que se elige en la leyenda sigue elegida al
   *  cambiar de métrica o rango, aunque cambie de posición. */
  id: string;
  name: string;
  color: string;
  hasPlan: boolean;
  hasForecast: boolean;
}

/** Primer y último día de la subcampaña: sus fechas o, si faltan, su serie. */
const flight = (sub: ClientPortalSubcampaign): [string, string] | null => {
  const start = sub.startDate ?? sub.series[0]?.date;
  const end = sub.endDate ?? sub.series[sub.series.length - 1]?.date;
  return start && end ? [start, end] : null;
};

/**
 * Días de "todo": del primer día con datos de la empresa al último día de las
 * subcampañas que ya tienen datos (para ver su plan y su predicción), y como
 * poco hasta hoy. Sin datos, el mes actual.
 */
const allTimeDays = (subs: ClientPortalSubcampaign[]): string[] => {
  let start: string | null = null;
  let end = dayjs().format("YYYY-MM-DD");
  for (const sub of subs) {
    const first = sub.series.find((p) => p.impressions !== null)?.date;
    if (!first) continue;
    if (!start || first < start) start = first;
    const last = flight(sub)?.[1];
    if (last && last > end) end = last;
  }
  if (!start) {
    const month = dayjs().startOf("month");
    return Array.from({ length: month.daysInMonth() }, (_, i) =>
      month.add(i, "day").format("YYYY-MM-DD")
    );
  }
  const days: string[] = [];
  for (let d = dayjs(start); !d.isAfter(dayjs(end)); d = d.add(1, "day"))
    days.push(d.format("YYYY-MM-DD"));
  return days;
};

/**
 * Lo que abarca la gráfica del inicio: del primer día con datos al último de
 * las subcampañas con datos (el atajo "Toda la campaña" del selector). El
 * inicio usa lo mismo para sus cifras.
 */
export const homeBounds = (subs: ClientPortalSubcampaign[]): RangeBounds => {
  const days = allTimeDays(subs);
  return { min: days[0], max: days[days.length - 1] };
};

/** Corre algún día entre `from` y `to` (YYYY-MM-DD). */
const runsBetween = (
  sub: ClientPortalSubcampaign,
  from: string,
  to: string
) => {
  const range = flight(sub);
  return range !== null && range[0] <= to && range[1] >= from;
};

interface MonthValues {
  delivered: (number | null)[];
  /** Lo previsto: solo si la métrica es el KPI de la subcampaña y tiene plan. */
  planned: (number | null)[] | null;
  /** Predicción: desde el último día con datos, cada día suma lo mismo que ese. */
  forecast: (number | null)[] | null;
}

/**
 * Valores de cada día del mes para una subcampaña. En acumulado se suma desde
 * el inicio de la subcampaña, aunque empezara en un mes anterior: es lo que
 * lleva conseguido. El CTR acumulado es clics acumulados entre impresiones
 * acumuladas. Los días en que la subcampaña no corre quedan sin valor.
 */
const monthValues = (
  sub: ClientPortalSubcampaign,
  days: string[],
  metric: MonthMetric,
  chartMode: ChartMode
): MonthValues => {
  const byDate = new Map(sub.series.map((point) => [point.date, point]));
  // La inversión y su plan llegan acumulados: aquí se usan los de cada día.
  const dailyByDate = new Map(
    toDailySeries(sub.series).map((point) => [point.date, point])
  );
  const isMoney = metric === "investment";
  const planOf = (point: ClientPortalSubcampaign["series"][number]) =>
    isMoney ? point.plannedInvestment : point.planned;
  const valueOf = (impressions: number, clicks: number, investment: number) =>
    metric === "impressions"
      ? impressions
      : metric === "clicks"
        ? clicks
        : metric === "investment"
          ? investment
          : impressions > 0
            ? clicks / impressions
            : null;

  // La predicción arranca en el último día con datos para enlazar con la línea
  // real, y solo si a la subcampaña aún le quedan días.
  const lastDay = [...sub.series].reverse().find((p) => p.impressions !== null);
  const lastSeriesDate = sub.series[sub.series.length - 1]?.date;
  const hasForecast = !!lastDay && lastSeriesDate > lastDay.date;
  const hasPlan = isMoney
    ? sub.budget != null
    : metric === sub.kpi && sub.planned != null;
  // Lo que la subcampaña ya llevaba antes del mes: el acumulado parte de ahí.
  // El plan llega acumulado desde su inicio; en diario se resta el día anterior.
  const earlier = sub.series.filter((p) => p.date < days[0]);
  const before = earlier[earlier.length - 1];

  const delivered: (number | null)[] = [];
  const planned: (number | null)[] = [];
  const forecast: (number | null)[] = [];
  let impressions = earlier.reduce((sum, p) => sum + (p.impressions ?? 0), 0);
  let clicks = earlier.reduce((sum, p) => sum + (p.clicks ?? 0), 0);
  // En un mes futuro los días de antes aún no tienen dato: vale el último real.
  let investment =
    [...earlier].reverse().find((p) => p.investment !== null)?.investment ?? 0;
  let planBefore = (before && planOf(before)) ?? 0;
  for (const date of days) {
    const point = byDate.get(date);
    if (!point) {
      delivered.push(null);
      planned.push(null);
      forecast.push(null);
      continue;
    }
    const real = point.impressions !== null;
    const source = real ? point : hasForecast ? lastDay : null;
    const dayImpressions = source?.impressions ?? 0;
    const dayClicks = source?.clicks ?? 0;
    const dayInvestment =
      (source && dailyByDate.get(source.date)?.investment) ?? 0;
    impressions += dayImpressions;
    clicks += dayClicks;
    investment += dayInvestment;
    const value =
      chartMode === "daily"
        ? valueOf(dayImpressions, dayClicks, dayInvestment)
        : valueOf(impressions, clicks, investment);
    delivered.push(real ? value : null);
    forecast.push(
      hasForecast && lastDay && date >= lastDay.date ? value : null
    );
    const plan = planOf(point);
    if (plan === null) planned.push(null);
    else {
      planned.push(chartMode === "daily" ? plan - planBefore : plan);
      planBefore = plan;
    }
  }
  return {
    delivered,
    planned: hasPlan ? planned : null,
    forecast: hasForecast ? forecast : null,
  };
};

/**
 * Gráfica principal del inicio: todas las subcampañas que corren en el rango
 * de fechas elegido, una línea cada una, con impresiones, clics, CTR o
 * inversión. Arranca en toda la campaña.
 */
const MonthChartViewClient: React.FC<{
  campaigns: ClientPortalCampaign[];
  /** Moneda de todas las campañas; sin ella (varias monedas) no hay inversión. */
  currency?: string | null;
  /** Rango de fechas: lo lleva el inicio, que lo comparte con sus cifras. */
  range: DateRange;
  onRangeChange: (range: DateRange) => void;
  /** Último día con datos (ayer). */
  asOfDate: string;
  /** Sin tarjeta: la gráfica flota sobre el fondo de la página. */
  floating?: boolean;
}> = ({ campaigns, currency, range, onRangeChange, asOfDate, floating }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const language = i18n.language;
  const paletteMode = theme.palette.mode;
  const [chartMode, setChartMode] = useChartMode();
  const [metric, setMetric] = useState<MonthMetric>("impressions");
  // Al pasar a una empresa con varias monedas deja de haber inversión.
  useEffect(() => {
    if (!currency && metric === "investment") setMetric("impressions");
  }, [currency, metric]);

  const subs = useMemo(
    () => allSubcampaigns(campaigns).map(({ sub }) => sub),
    [campaigns]
  );
  const bounds = useMemo(() => homeBounds(subs), [subs]);
  const { from, to } = resolveRange(range, bounds, asOfDate);
  const isAllTime = range.preset === "lifetime";

  const { rows, series, hidden } = useMemo(() => {
    const days = rangeDays(from, to);
    // Si hay más subcampañas que colores, se ven las de más impresiones.
    const running = subs
      .filter((sub) => runsBetween(sub, from, to))
      .map((sub) => ({
        sub,
        values: monthValues(sub, days, metric, chartMode),
        weight: monthValues(
          sub,
          days,
          "impressions",
          "cumulative"
        ).delivered.reduce<number>((last, value) => value ?? last, 0),
      }))
      .sort((a, b) => b.weight - a.weight);
    const shown = running.slice(0, MAX_SERIES);

    return {
      rows: days.map((date, day) => {
        const row: Row = { date };
        shown.forEach(({ values }, index) => {
          row[`d${index}`] = values.delivered[day];
          row[`p${index}`] = values.planned?.[day] ?? null;
          row[`f${index}`] = values.forecast?.[day] ?? null;
        });
        return row;
      }),
      series: shown.map<Series>(({ sub, values }, index) => ({
        key: String(index),
        id: sub.id,
        name: sub.name,
        // Aquí cada línea es una campaña: colores bien distintos, sea cual sea
        // la métrica (no el azul, naranja o verde de cada una).
        color: seriesColor(index, paletteMode),
        hasPlan: values.planned !== null,
        hasForecast: values.forecast !== null,
      })),
      hidden: running.length - shown.length,
    };
  }, [subs, from, to, metric, chartMode, paletteMode]);

  // Clicar una subcampaña de la leyenda deja solo esa en la gráfica; volver a
  // clicarla (o que salga del rango) las muestra todas otra vez.
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const focused = series.find((s) => s.id === focusedId) ?? null;
  const visibleSeries = focused ? [focused] : series;

  const isPercent = metric === "ctr";
  const money = metric === "investment" && currency ? currency : null;
  const formatValue = (value: number) =>
    isPercent
      ? formatPercent(value, language, 1)
      : money
        ? formatMoney(value, money, language)
        : formatCompact(value, language);
  const formatExact = (value: number) =>
    isPercent
      ? formatPercent(value, language, 2)
      : money
        ? formatUnitMoney(value, money, language)
        : formatNumber(value, language);
  const muted = theme.palette.neutral[600];

  const renderTooltip = ({
    active,
    payload,
    label,
  }: TooltipProps<number, string>) => {
    if (!active || !payload?.length) return null;
    const row = payload[0].payload as Row;
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "14px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          px: 2,
          py: 1.5,
          minWidth: 200,
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>
          {formatShortDate(String(label), language)}
        </Typography>
        {visibleSeries.map((s) => {
          const delivered = row[`d${s.key}`];
          const planned = row[`p${s.key}`];
          // El día que enlaza la predicción con lo real ya sale como conseguido.
          const forecast = delivered == null ? row[`f${s.key}`] : null;
          if (delivered == null && planned == null && forecast == null)
            return null;
          const details = [
            delivered != null &&
              `${t("clientView.charts.delivered")} ${formatExact(Number(delivered))}`,
            forecast != null &&
              `${t("clientView.charts.forecast")} ${formatExact(Number(forecast))}`,
            planned != null &&
              `${t("clientView.charts.planned")} ${formatExact(Number(planned))}`,
          ].filter(Boolean);
          return (
            <Box key={s.key} sx={{ mb: 0.75 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: s.color,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    maxWidth: 220,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {s.name}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: "text.secondary", pl: 2 }}>
                {details.join(" · ")}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  const controls = (
    <Box
      sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}
    >
      <PillToggle
        value={metric}
        onChange={setMetric}
        label={t("clientView.charts.metricLabel")}
        options={[
          ...METRICS,
          ...(currency ? (["investment"] as const) : []),
        ].map((m) => ({
          value: m,
          label: t(`clientView.stats.${m}`),
        }))}
      />
      <ChartModeSwitch value={chartMode} onChange={setChartMode} />
      <DateRangePickerViewClient
        value={range}
        onChange={onRangeChange}
        bounds={bounds}
        yesterday={asOfDate}
      />
    </Box>
  );

  return (
    <ClientCard
      floating={floating}
      title={t(
        isAllTime
          ? "clientView.charts.allTimeTitle"
          : "clientView.charts.monthTitle"
      )}
      action={controls}
    >
      {series.length === 0 ? (
        <Box
          sx={{
            height: CHART_HEIGHT,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ color: "text.secondary", fontSize: 15 }}>
            {t(
              isAllTime
                ? "clientView.charts.noData"
                : "clientView.charts.noDataMonth"
            )}
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ width: "100%", height: CHART_HEIGHT }}>
            <ResponsiveContainer>
              <ComposedChart
                data={rows}
                margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
              >
                <CartesianGrid
                  vertical={false}
                  stroke={theme.palette.divider}
                  strokeDasharray="0"
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d: string) => formatShortDate(d, language)}
                  tick={{ fill: muted, fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  minTickGap={40}
                  tickMargin={10}
                />
                <YAxis
                  tickFormatter={formatValue}
                  tick={{ fill: muted, fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  width={56}
                />
                <Tooltip
                  content={renderTooltip}
                  cursor={
                    chartMode === "daily"
                      ? { fill: theme.palette.neutral[100] }
                      : { stroke: theme.palette.neutral[400], strokeWidth: 1 }
                  }
                />
                {progressSeriesElements({
                  series: visibleSeries,
                  daily: chartMode === "daily",
                  stacked: metric !== "ctr",
                  background: theme.palette.background.paper,
                  forecastColor: theme.palette.neutral[400],
                })}
              </ComposedChart>
            </ResponsiveContainer>
          </Box>

          {/* Leyenda: qué color es cada subcampaña; cada una es un botón que
              deja solo esa en la gráfica. */}
          <Box
            sx={{
              mt: 3,
              // El relleno de los botones no mueve la leyenda de su sitio.
              ml: -1,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              columnGap: 1,
              rowGap: 0.5,
            }}
          >
            {series.map((s) => (
              <ButtonBase
                key={s.key}
                onClick={() => setFocusedId(focused?.id === s.id ? null : s.id)}
                aria-pressed={focused?.id === s.id}
                title={t(
                  focused?.id === s.id
                    ? "clientView.charts.showAllSeries"
                    : "clientView.charts.showOnlySeries"
                )}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minWidth: 0,
                  px: 1,
                  py: 0.75,
                  borderRadius: "999px",
                  textAlign: "left",
                  // En reposo se ve como antes; el botón aparece al pasar por encima.
                  opacity: focused && focused.id !== s.id ? 0.45 : 1,
                  backgroundColor:
                    focused?.id === s.id
                      ? alpha(theme.palette.text.primary, 0.08)
                      : "transparent",
                  transition: "background-color 0.15s ease, opacity 0.15s ease",
                  "&:hover": {
                    opacity: 1,
                    backgroundColor: alpha(theme.palette.text.primary, 0.08),
                  },
                  "&.Mui-focusVisible": {
                    outline: `2px solid ${theme.palette.text.primary}`,
                    outlineOffset: 1,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: s.color,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 13,
                    maxWidth: 260,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {s.name}
                </Typography>
              </ButtonBase>
            ))}
            {hidden > 0 && (
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                {t("clientView.charts.moreSeries", { count: hidden })}
              </Typography>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
              <LegendStroke
                label={t("clientView.charts.delivered")}
                color={muted}
                bar={chartMode === "daily" ? "solid" : undefined}
              />
              {visibleSeries.some((s) => s.hasForecast) && (
                <LegendStroke
                  label={t("clientView.charts.forecast")}
                  color={muted}
                  dash="1 4"
                  bar={chartMode === "daily" ? "faded" : undefined}
                />
              )}
              {visibleSeries.some((s) => s.hasPlan) && (
                <LegendStroke
                  label={t("clientView.charts.planned")}
                  color={muted}
                  dash="5 4"
                />
              )}
            </Box>
          </Box>
        </>
      )}
    </ClientCard>
  );
};

export default MonthChartViewClient;
